import { FileText, FileSpreadsheet, Image, File, Upload } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import SignaturePad from './SignaturePad'
import ClientSignViewer from '../components/ClientSignViewer'
import DocumentDownloadButton from '../components/DocumentDownloadButton'
import DocumentUploadModal from '../components/DocumentUploadModal'
import { hasSignedDocument, signedDocumentUnavailable } from '../lib/documentDownloads'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const STATUS_COLORS = { pending: 'warning', reviewed: 'success', needs_resubmission: 'danger', rejected: 'danger', signed: 'success', sent: 'neutral', viewed: 'success', awaiting_signature: 'warning' }
const STATUS_LABELS = { pending: 'Pending Review', reviewed: 'Reviewed ✓', needs_resubmission: 'Needs Resubmission', rejected: 'Rejected', signed: 'Signed ✍️', sent: 'Sent', viewed: 'Viewed ✓', awaiting_signature: 'Awaiting Signature ✍️' }

function fileIcon(name) {
  const ext = name?.split('.').pop().toLowerCase()
  const Icon = ['xlsx', 'xls', 'csv'].includes(ext) ? FileSpreadsheet : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? Image : ['pdf', 'doc', 'docx'].includes(ext) ? FileText : File
  return <Icon size={22} aria-hidden="true"/>
}

function formatSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card animate-fadeUp" style={{ width: '100%', maxWidth: 520, padding: 0 }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 17 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#62615D', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '22px' }}>{children}</div>
      </div>
    </div>
  )
}

export default function DocumentsPage() {
  const { profile, user, isCPA } = useAuth()
  const navigate = useNavigate()
  const [clients, setClients] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [filterClient, setFilterClient] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [reviewNote, setReviewNote] = useState('')
  const [toast, setToast] = useState('')
  const [uploadClientId, setUploadClientId] = useState('')
  const [uploadClientContext, setUploadClientContext] = useState({})
  const [signingDoc, setSigningDoc] = useState(null)
  const [signingViewerDoc, setSigningViewerDoc] = useState(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [pendingFiles, setPendingFiles] = useState([])
  const fileInputRef = useRef()

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000) }

  useEffect(() => { loadData() }, [])

  async function loadData() {
    if (isCPA) {
      const { data: c } = await supabase.from('clients').select('id, full_name, business_name').eq('cpa_id', profile.id)
      setClients(c || [])
      const { data: d } = await supabase.from('documents')
        .select('*, clients(full_name, business_name)')
        .in('client_id', (c || []).map(x => x.id))
        .order('created_at', { ascending: false })
      setDocuments(d || [])
    } else {
      const { data: client } = await supabase.from('clients').select('id, cpa_id, tax_year').eq('portal_user_id', profile?.id || user?.id).maybeSingle()
      if (client) {
        const { data: d } = await supabase.from('documents').select('*').eq('client_id', client.id).order('created_at', { ascending: false })
        setDocuments(d || [])
        setUploadClientId(client.id)
        setUploadClientContext({ cpaId: client.cpa_id, taxYear: client.tax_year })
      }
    }
    setLoading(false)
  }

  async function logActivity(action, doc, extra = {}) {
    try {
      await supabase.from('activity_log').insert({
        client_id: doc.client_id || null,
        actor_id: profile?.id || null,
        action,
        details: {
          file_name: doc.file_name,
          client_name: doc.clients?.business_name || doc.clients?.full_name || '',
          performed_by_email: profile?.email,
          ...extra
        }
      })
    } catch (err) {
      console.warn('Activity log error:', err)
    }
  }

  // Client marking an accountant-sent document as viewed (narrow security-definer RPC)
  async function markViewed(doc) {
    if (isCPA || doc.uploaded_by !== 'cpa' || doc.viewed_at) return
    try {
      await supabase.rpc('mark_document_viewed', { p_document_id: doc.id })
      const patch = { viewed_at: new Date().toISOString(), status: doc.status === 'sent' ? 'viewed' : doc.status }
      setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, ...patch } : d))
      setSelectedDoc(prev => (prev?.id === doc.id ? { ...prev, ...patch } : prev))
    } catch (err) {
      console.warn('markViewed error:', err)
    }
  }

  async function handleSign(signatureData) {
    if (!signingDoc) return
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Please sign in again before signing.')
      const res = await fetch('/.netlify/functions/save-signature', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          document_id: signingDoc.id,
          client_id: signingDoc.client_id,
          signature_data: signatureData
        })
      })
      const result = await res.json()
      if (!res.ok || !result.success) throw new Error(result.error || 'The signature could not be saved. Please try again.')
      if (result.success) {
        setDocuments(prev => prev.map(d => d.id === signingDoc.id ? { ...d, status: 'signed' } : d))
        if (selectedDoc?.id === signingDoc.id) setSelectedDoc(prev => ({ ...prev, status: 'signed' }))
        notify('Document signed successfully!')
      }
    } catch (err) {
      throw err
    }
    setSigningDoc(null)
  }

  function handleUploadResult(uploaded, { complete, allUploaded }) {
    if (uploaded.length) {
      setDocuments(prev => [...uploaded, ...prev.filter(doc => !uploaded.some(saved => saved.id === doc.id))])
      uploaded.forEach(doc => logActivity(isCPA ? 'document_sent' : 'document_uploaded', doc, {
        category: doc.category, tax_year: doc.tax_year, requires_signature: doc.requires_signature
      }))
      notify(isCPA ? `${uploaded.length} file(s) sent to client!` : `${uploaded.length} file(s) uploaded successfully!`)
    }
    const sent = isCPA ? uploaded.filter(doc => !doc.requires_signature) : []
    if (sent.length) {
      // Signature requests are still notified after the firm confirms placement.
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session?.access_token) return
        return fetch('/.netlify/functions/notify-client', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ type: 'document_sent', client_id: sent[0].client_id, data: { file_names: sent.map(doc => doc.file_name), requires_signature: false } })
        })
      }).catch(err => console.warn('Notification error:', err))
    }
    if (complete) {
      setShowUploadModal(false)
      setPendingFiles([])
      const signatureDoc = isCPA ? allUploaded.find(doc => doc.requires_signature) : null
      if (signatureDoc) navigate(`/document-editor/${signatureDoc.id}`)
    }
  }

  function queueFiles(files) {
    if (uploading || showUploadModal || !files.length) return
    setPendingFiles(files)
    setShowUploadModal(true)
  }

  function onDrop(e) {
    e.preventDefault(); setDragOver(false)
    queueFiles(Array.from(e.dataTransfer.files))
  }

  function onFileSelect(e) {
    queueFiles(Array.from(e.target.files))
    e.target.value = ''
  }

  function onDocumentDownloaded(doc, result) {
    logActivity('document_downloaded', doc, { copy: result.variant })
    markViewed(doc)
  }

  async function updateDocStatus(docId, status, note) {
    const { data } = await supabase.from('documents')
      .update({ status, reviewer_note: note || null })
      .eq('id', docId)
      .select('*, clients(full_name, business_name)')
      .single()
    if (data) {
      setDocuments(prev => prev.map(d => d.id === docId ? data : d))
      setSelectedDoc(data)
      notify(`Document marked as ${STATUS_LABELS[status]}`)
      logActivity('document_status_changed', data, { new_status: status, note })

      // Notify client by email
      if (status === 'reviewed' || status === 'needs_resubmission') {
        supabase.auth.getSession().then(({ data: { session } }) => {
          fetch('/.netlify/functions/notify-client', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`
            },
            body: JSON.stringify({
              type: 'document_reviewed',
              client_id: data.client_id,
              data: { file_name: data.file_name, status, note }
            })
          }).catch(err => console.warn('Notification error:', err))
        })
      }
    }
  }

  async function deleteDoc(doc) {
    logActivity('document_deleted', doc)
    await supabase.storage.from('client-documents').remove([doc.file_path])
    await supabase.from('documents').delete().eq('id', doc.id)
    setDocuments(prev => prev.filter(d => d.id !== doc.id))
    setSelectedDoc(null)
    notify('Document deleted')
  }

  const filtered = documents.filter(d => {
    const matchClient = filterClient === 'all' || d.client_id === filterClient
    const matchStatus = filterStatus === 'all' || d.status === filterStatus
    return matchClient && matchStatus
  })

  // Client view splits accountant-sent documents from the client's own uploads
  const fromAccountant = filtered.filter(d => d.uploaded_by === 'cpa')
  const ownUploads = filtered.filter(d => d.uploaded_by !== 'cpa')

  const pendingCount = documents.filter(d => d.status === 'pending').length
  const resubCount = documents.filter(d => d.status === 'needs_resubmission').length

  if (loading) return (
    <div style={{ display: 'grid', gap: 10 }}>
      {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 64 }} />)}
    </div>
  )

  function renderTable(docs, emptyTitle, emptyBody) {
    return (
      <div className="card" style={{ overflow: 'hidden' }}>
        {docs.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <h3 style={{ marginBottom: 8 }}>{emptyTitle}</h3>
            <p style={{ color: '#62615D', fontSize: 13 }}>{emptyBody}</p>
          </div>
        ) : (
          <div className="documents-table-scroll">
          <table className="documents-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F7F3EB' }}>
                {['File', isCPA && 'Client', 'Category', 'Size', 'Uploaded', 'Status', ''].filter(Boolean).map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#62615D', letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id} style={{ borderBottom: '1px solid #F7F3EB', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FAFAF7'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => { setSelectedDoc(doc); setReviewNote(doc.reviewer_note || ''); markViewed(doc) }}
                >
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 20 }}>{fileIcon(doc.file_name)}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#202326' }}>{doc.file_name}</div>
                        {doc.reviewer_note && <div style={{ fontSize: 11, color: '#EF4444' }}>⚠ {doc.reviewer_note}</div>}
                      </div>
                    </div>
                  </td>
                  {isCPA && <td style={{ padding: '12px 14px', fontSize: 12, color: '#4a5568' }}>{doc.clients?.business_name || doc.clients?.full_name || '—'}</td>}
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#4a5568' }}>{doc.category || '—'}</td>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#62615D' }}>{formatSize(doc.file_size)}</td>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#62615D' }}>{new Date(doc.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span className={`badge badge-${STATUS_COLORS[doc.status] || 'neutral'}`} style={{ fontSize: 10 }}>
                      {STATUS_LABELS[doc.status] || doc.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <DocumentDownloadButton doc={doc} variant={hasSignedDocument(doc) ? 'signed' : 'original'}
                      style={{ padding: '4px 10px', fontSize: 11 }} onDownloaded={result => onDocumentDownloaded(doc, result)}>
                      {hasSignedDocument(doc) ? 'Download signed PDF' : (doc.requires_signature || signedDocumentUnavailable(doc)) ? 'Download original' : 'Download'}
                    </DocumentDownloadButton>
                    {signedDocumentUnavailable(doc) && <div style={{ fontSize: 11, color: '#92400E', marginTop: 5 }}>Signed PDF unavailable</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    )
  }

  const canSignSelected = selectedDoc && !isCPA && (
    selectedDoc.uploaded_by === 'cpa'
      ? ['pending', 'partial'].includes(selectedDoc.signing_status)
      : selectedDoc.status === 'awaiting_signature'
  )

  return (
    <div className="documents-page-root">
      <style>{`
        @media (max-width: 768px) {
          .documents-page-root .documents-stats {
            grid-template-columns: 1fr 1fr !important;
          }
          .documents-page-root .documents-filters .input {
            width: 100% !important;
          }
          .documents-page-root .documents-table-scroll {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .documents-page-root .documents-table {
            min-width: 760px;
          }
          .documents-page-root .documents-modal-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <div className="cf-page-intro"><div><p className="cf-overline">DOCUMENT WORKSPACE</p><h2>Every file. Clear context.</h2><p>{isCPA ? 'Review client submissions, share documents and keep signatures moving.' : 'Upload the files your firm needs and review documents shared with you.'}</p></div></div>
      {/* Stats row */}
      <div className="documents-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Documents', value: documents.length, color: '#CBB17B' },
          { label: 'Pending Review', value: pendingCount, color: '#F59E0B' },
          { label: 'Reviewed', value: documents.filter(d => d.status === 'reviewed').length, color: '#10B981' },
          { label: 'Needs Resubmission', value: resubCount, color: '#EF4444' },
        ].map((s, i) => (
          <div key={i} className="card animate-fadeUp" style={{ padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.color }} />
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 26, fontWeight: 700 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); if (!uploading && !showUploadModal) setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        role="button" tabIndex={uploading ? -1 : 0} aria-label="Upload documents" aria-disabled={uploading}
        onKeyDown={e => { if (!uploading && !showUploadModal && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); fileInputRef.current?.click() } }}
        onClick={() => { if (!uploading && !showUploadModal) fileInputRef.current?.click() }}
        style={{
          border: `2px dashed ${dragOver ? '#CBB17B' : '#D4C9B5'}`,
          borderRadius: 12, padding: '28px', textAlign: 'center',
          background: dragOver ? '#F1E8D7' : '#FAFAF7',
          marginBottom: 20, transition: 'all 0.2s', cursor: 'pointer'
        }}
      >
        <div style={{ color: '#765B2C', marginBottom: 8 }}><Upload size={28}/></div>
        <div style={{ fontSize: 14, fontWeight: 600, color: dragOver ? '#765B2C' : '#202326' }}>
          {uploading ? (isCPA ? 'Sending…' : 'Uploading…') : (isCPA ? 'Drop files here to send to a client' : 'Drop files here or click to browse')}
        </div>
        <div style={{ fontSize: 12, color: '#62615D', marginTop: 4 }}>PDF, Excel, images, Word documents</div>
        <input ref={fileInputRef} type="file" multiple disabled={uploading} style={{ display: 'none' }} onChange={onFileSelect} accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp,.doc,.docx" />
      </div>

      {/* Filters */}
      <div className="documents-filters" style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {isCPA && (
          <select aria-label="Filter by client" className="input" style={{ width: 200 }} value={filterClient} onChange={e => setFilterClient(e.target.value)}>
            <option value="all">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.full_name}</option>)}
          </select>
        )}
        <select aria-label="Filter by status" className="input" style={{ width: 180 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="pending">Pending Review</option>
          <option value="reviewed">Reviewed</option>
          <option value="needs_resubmission">Needs Resubmission</option>
          <option value="sent">Sent</option>
          <option value="viewed">Viewed</option>
          <option value="awaiting_signature">Awaiting Signature</option>
          <option value="signed">Signed</option>
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: '#62615D', alignSelf: 'center' }}>{filtered.length} document{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Document tables */}
      {isCPA ? (
        renderTable(filtered, 'No documents yet', 'Upload files using the drop zone above')
      ) : (
        <>
          {fromAccountant.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#202326', marginBottom: 10 }}>📥 From Your Accountant</h3>
              {renderTable(fromAccountant, '', '')}
            </div>
          )}
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#202326', marginBottom: 10 }}>📤 Your Uploads</h3>
          {renderTable(ownUploads, 'No documents yet', 'Upload files using the drop zone above')}
        </>
      )}

      {/* Upload modal */}
      {showUploadModal && (
        <DocumentUploadModal files={pendingFiles} clients={clients} initialClientId={uploadClientId}
          initialCpaId={uploadClientContext.cpaId} initialTaxYear={uploadClientContext.taxYear}
          isCPA={isCPA} profileId={profile?.id} onBusyChange={setUploading} onResult={handleUploadResult}
          onClose={() => { if (!uploading) { setShowUploadModal(false); setPendingFiles([]) } }} />
      )}

      {/* Document detail / review modal */}
      {selectedDoc && (
        <Modal title="Document Details" onClose={() => setSelectedDoc(null)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '14px', background: '#F7F3EB', borderRadius: 10 }}>
            <span style={{ fontSize: 32 }}>{fileIcon(selectedDoc.file_name)}</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{selectedDoc.file_name}</div>
              <div style={{ fontSize: 12, color: '#62615D' }}>{selectedDoc.category} · {formatSize(selectedDoc.file_size)} · {new Date(selectedDoc.created_at).toLocaleDateString()}</div>
              {isCPA && <div style={{ fontSize: 12, color: '#4a5568', marginTop: 2 }}>Client: {selectedDoc.clients?.business_name || selectedDoc.clients?.full_name}</div>}
              {isCPA && selectedDoc.uploaded_by === 'cpa' && (
                <div style={{ fontSize: 12, color: selectedDoc.viewed_at ? '#065F46' : '#92400E', marginTop: 2 }}>
                  {selectedDoc.viewed_at ? `👁 Viewed by client ${new Date(selectedDoc.viewed_at).toLocaleDateString()}` : '⏳ Not yet viewed by client'}
                </div>
              )}
            </div>
            <span className={`badge badge-${STATUS_COLORS[selectedDoc.status]}`} style={{ marginLeft: 'auto' }}>
              {STATUS_LABELS[selectedDoc.status]}
            </span>
          </div>

          {isCPA && selectedDoc.uploaded_by !== 'cpa' && (
            <div style={{ marginBottom: 16 }}>
              <label className="label">Review Note (shown to client)</label>
              <textarea className="input" placeholder="Optional note for the client…" value={reviewNote} onChange={e => setReviewNote(e.target.value)} rows={3} style={{ resize: 'vertical' }} />
            </div>
          )}

          {hasSignedDocument(selectedDoc) && <p style={{ fontSize: 13, color: '#065F46', marginBottom: 16 }}>Your completed signed PDF is ready to download.</p>}
          {signedDocumentUnavailable(selectedDoc) && <p role="status" style={{ fontSize: 13, color: '#92400E', marginBottom: 16 }}>A signature was recorded, but a signed PDF is not available for this document. The original is available separately.</p>}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            {(isCPA || selectedDoc.uploaded_by !== 'cpa') ? (
              <button className="btn btn-danger" style={{ fontSize: 12 }} onClick={() => { if (window.confirm('Delete this document?')) deleteDoc(selectedDoc) }}>
                🗑 Delete
              </button>
            ) : <div />}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {hasSignedDocument(selectedDoc) && <DocumentDownloadButton doc={selectedDoc} className="btn btn-primary" onDownloaded={result => onDocumentDownloaded(selectedDoc, result)} />}
              <DocumentDownloadButton doc={selectedDoc} variant="original" onDownloaded={result => onDocumentDownloaded(selectedDoc, result)} />
              {isCPA && selectedDoc.uploaded_by === 'cpa' && selectedDoc.requires_signature && (
                <a href={`/document-editor/${selectedDoc.id}`}
                  className="btn btn-primary"
                  style={{ fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  ✍️ Place Signatures
                </a>
              )}
              {isCPA && selectedDoc.uploaded_by !== 'cpa' && selectedDoc.status !== 'reviewed' && (
                <button className="btn btn-primary" style={{ fontSize: 12, background: '#D1FAE5', color: '#065F46' }}
                  onClick={() => updateDocStatus(selectedDoc.id, 'reviewed', reviewNote)}>
                  ✓ Mark Reviewed
                </button>
              )}
              {isCPA && selectedDoc.uploaded_by !== 'cpa' && selectedDoc.status !== 'needs_resubmission' && (
                <button className="btn btn-primary" style={{ fontSize: 12, background: '#FEE2E2', color: '#991B1B' }}
                  onClick={() => updateDocStatus(selectedDoc.id, 'needs_resubmission', reviewNote)}>
                  ↩ Request Resubmission
                </button>
              )}
              {canSignSelected && (
                <button className="btn btn-gold" style={{ fontSize: 12 }}
                  onClick={() => {
                    if (selectedDoc.uploaded_by === 'cpa' && ['pending','partial'].includes(selectedDoc.signing_status)) {
                      setSigningViewerDoc(selectedDoc)
                    } else {
                      setSigningDoc(selectedDoc)   // legacy pad for docs without placed fields
                    }
                  }}>
                  ✍️ Sign Document
                </button>
              )}
              {selectedDoc.status === 'signed' && (
                <div style={{ fontSize: 12, color: '#065F46', fontWeight: 700, padding: '8px 12px', background: '#D1FAE5', borderRadius: 8 }}>
                  ✍️ Signed
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Signature Pad */}
      {signingDoc && (
        <SignaturePad
          documentName={signingDoc.file_name}
          onSign={handleSign}
          onClose={() => setSigningDoc(null)}
        />
      )}

      {/* Client signing viewer */}
      {signingViewerDoc && (
        <ClientSignViewer
          key={signingViewerDoc.id}
          doc={signingViewerDoc}
          onClose={() => setSigningViewerDoc(null)}
          onComplete={(result) => {
            setDocuments(prev => prev.map(d => d.id === signingViewerDoc.id
              ? { ...d, status: result.complete ? 'signed' : d.status, signing_status: result.complete ? 'complete' : 'partial', signed_file_path: result.signed_file_path || d.signed_file_path }
              : d))
            setSelectedDoc(null)
            setSigningViewerDoc(null)
            notify(result.complete ? 'All signatures complete!' : 'Signature saved — more signers remaining')
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, background: '#202326', color: '#fff', padding: '11px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: '0 8px 28px rgba(0,0,0,0.22)', animation: 'toastUp 0.2s ease' }}>
          ✓ {toast}
        </div>
      )}
    </div>
  )
}
